import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, FileText, History, Lock, MessageSquarePlus, Send, Siren, Undo2 } from 'lucide-react';
import type { CursorPage, LabReport, LabReportPage, LabResultCounts, PendingResultRow } from '@dru/types';
import { FLAG_LABEL, flagWord } from '@dru/types';
import {
  Alert, Button, Card, ConfirmDialog, Dialog, EmptyState, ErrorState, IconAction, LoadMore, RowActions, SearchInput, StatusBadge, Tabs, TextField, TextSkeleton, cn,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { usePages } from '../../../lib/usePages';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { useVenueFilter } from '../../hospital/usePartner';
import { eitherOf, useSigners } from '../../../lib/labSignoff';
import { subjectLine } from './Worklist';
import { flagTone } from '../../hospital/routes/Encounter';
import { SignatureReadiness } from '../components/SignatureReadiness';
import { ClinicalComposer, canCompose } from '../../../components/ClinicalComposer';
import { countText, loadedOf } from '../lib/counts';
import { useBackLink, useLabLinks } from '../lib/context';

/**
 * Validate, then release.
 *
 * Two states and two acts, deliberately. Validation is the pathologist saying
 * the value is right; release is the moment it becomes visible to the
 * physician and the worker. Collapsing them would mean a value reaching a
 * fitness decision at the instant somebody first looked at it. So the two are
 * drawn differently — validate is a check, release is the green act with a
 * confirmation that says who will see what.
 *
 * Each subject is reviewed as one card, findings first: critical, then out of
 * range, with the normal values folded underneath. The rule that nobody
 * validates or releases their own entry holds per row; the screen leaves
 * those rows out of the batch and says so before the click, not after a 403.
 *
 * Validating has a second answer. A value the pathologist does not believe —
 * a haemoglobin of 0, a decimal point in the wrong place — is sent back to
 * the bench with a reason, and leaves this queue until it is entered again.
 * Before that existed the only choices were to sign a number nobody believed
 * or to leave it blocking the subject for good.
 *
 * A login without the validate or release permission — a Lab Admin — sees the
 * queue and no controls at all, rather than buttons that would fail.
 */

type Stage = 'ENTERED' | 'VALIDATED' | 'ALL';

const isCritical = (r: PendingResultRow) => r.flag === 'CRITICAL_LOW' || r.flag === 'CRITICAL_HIGH';

export function Release() {
  const { permissions } = useAuth();
  const { query, partnerId } = useVenueFilter();
  // Tab and search live in the address: Back and a shared link keep them.
  const [params, setParams] = useSearchParams();
  const stage = ((params.get('tab') as Stage | null) ?? 'ENTERED') as Stage;
  const term = params.get('q') ?? '';
  const setStage = (k: Stage) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', k); return n; }, { replace: true });
  const [search, setSearch] = useState(term);
  // A link or Back that changes the address updates the box too, before
  // the box's own debounce could write its stale text back over it.
  useEffect(() => { setSearch(term); }, [term]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== term) setParams((p) => { const n = new URLSearchParams(p); if (search.trim()) n.set('q', search.trim()); else n.delete('q'); return n; }, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [nonce, setNonce] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // The subject open on the right (a dedicated view below lg), in the address
  // as `subject`, the key every lab screen uses for an encounter.
  const selectedId = params.get('subject') ?? '';
  const SCROLL = 'dru.release.listScroll';
  const select = (id: string | null) => {
    if (id) { try { sessionStorage.setItem(SCROLL, String(window.scrollY)); } catch { /* ignore */ } }
    setParams((p) => { const n = new URLSearchParams(p); if (id) n.set('subject', id); else n.delete('subject'); return n; }, { replace: false });
    if (id && !window.matchMedia('(min-width: 1024px)').matches) window.scrollTo({ top: 0 });
  };
  // Back to the list on a phone: the same tab, search and place in the list.
  const backToList = () => {
    select(null);
    // Restored once the list is back on screen (see the effect below).
    restoreScroll.current = true;
  };
  const back = useBackLink();
  const restoreScroll = useRef(false);
  useEffect(() => {
    if (selectedId || !restoreScroll.current) return;
    restoreScroll.current = false;
    let y = 0; try { y = Number(sessionStorage.getItem(SCROLL)) || 0; } catch { /* ignore */ }
    const t = setTimeout(() => window.scrollTo({ top: y }), 30);
    return () => clearTimeout(t);
  }, [selectedId]);

  // Everything outstanding, filtered here so each tab can carry its count.
  // Paged: a lab with a backlog has more than one page, and the rest used to
  // be unreachable. Counts carry a "+" while more pages exist.
  // In the server's order: subjects with an unacknowledged critical value
  // first, then acknowledged criticals, then anything out of range, oldest
  // waiting first — decided over every outstanding row, not one page.
  const pending = usePages<PendingResultRow>(
    (cursor) => api.get<CursorPage<PendingResultRow>>(`/lab/results?limit=100&stage=${stage}${query}${term ? `&q=${encodeURIComponent(term)}` : ''}${cursor ? `&cursor=${cursor}` : ''}`),
    [query, nonce, stage, term],
  );
  // Exact counts for the tabs and the critical banner, over everything outstanding.
  // Same venue and search as the queue, not the tab: one tab never zeroes another.
  const counts = useQuery<LabResultCounts>(() => api.get(`/lab/results/counts?x=1${query}${term ? `&q=${encodeURIComponent(term)}` : ''}`), [query, nonce, term]);

  const canValidate = holds(permissions, 'lab.result-validate');
  const canRelease = holds(permissions, 'lab.result-release');
  const canAck = holds(permissions, 'lab.critical-acknowledge');
  // Who else can sign what this login entered itself — named, so "a second
  // person" is somebody, or said plainly to be nobody.
  const signers = useSigners(partnerId || null);
  const others = signers.data?.partnerId
    ? signers.data.signers.filter((x) => !x.isYou).map((x) => x.fullName) : null;
  const [acking, setAcking] = useState<PendingResultRow | null>(null);
  const [returning, setReturning] = useState<PendingResultRow | null>(null);

  const all = pending.items;
  const rows = all; // filtered by stage on the server
  const groups = Array.from(
    rows.reduce((m, r) => { const l = m.get(r.encounterId) ?? []; l.push(r); m.set(r.encounterId, l); return m; },
      new Map<string, PendingResultRow[]>()).entries(),
  // Already in priority order from the server; a Map keeps first-seen order.
  );
  // The open subject: the one in the address, or (on a wide screen) the
  // first in the queue, which the server put first for a reason.
  const wide = typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;
  const active = groups.find(([id]) => id === selectedId) ?? (wide && !selectedId ? groups[0] : undefined);
  const activeId = active?.[0] ?? (selectedId || '');
  const c = counts.data;
  const criticalCount = c?.criticalOutstanding ?? all.filter(isCritical).length;
  const unackedCount = c?.unacknowledgedCritical
    ?? all.filter((r) => isCritical(r) && r.criticalAlert && !r.criticalAlert.acknowledgedAt).length;
  const reload = () => setNonce((n) => n + 1);
  const { pathname } = useLocation();
  // Neither signature is held: a technician or a lab admin reached this by its
  // address (the navigation does not offer it). They get the queue to read —
  // a lab admin acknowledges criticals from it — and no signing controls.
  const readOnly = !canValidate && !canRelease;
  const canEnter = holds(permissions, 'lab.result-enter');

  return (
    <div className="space-y-4">
      {back && (
        <Link to={back.to} className="inline-flex min-h-8 items-center gap-1 text-small font-medium text-primary-700 hover:underline max-md:min-h-10">
          <ArrowLeft aria-hidden className="h-4 w-4" />{back.label}
        </Link>
      )}
      <PageHeader
        title={readOnly ? 'Pathologist queue' : 'Validate & release'}
        hint={readOnly
          ? 'Results waiting for a pathologist to validate and release them. Read-only on this login.'
          : 'Two separate acts. Validating confirms a value is right. Releasing makes it visible to the physician and the employee — only released results reach a fitness decision.'}
        actions={<Button variant="secondary" size="sm" onClick={reload}>Refresh</Button>}
      />

      {readOnly && (
        <Alert tone="info" title="Validation and release are a pathologist’s">
          <Lock aria-hidden className="mr-1.5 inline h-4 w-4 align-text-bottom" />
          This login can see what is waiting but cannot validate or release it, and no one can sign a value they entered.
          {others && (others.length
            ? ` At this venue that is ${eitherOf(others)}.`
            : ' This venue has no Pathologist login yet, so nothing here can be signed out — the venue admin adds one under Staff.')}
          {canEnter && (
            <>
              {' '}
              <Link to={pathname.replace(/\/release$/, '/results')} className="font-semibold underline underline-offset-2">
                Back to result entry
              </Link>
            </>
          )}
        </Alert>
      )}

      {/* Below lg an open subject has the screen to itself: the queue's
          banners, tabs and search wait behind Back to list. */}
      <div className={cn('space-y-4', selectedId && 'max-lg:hidden')}>
      {!readOnly && <SignatureReadiness act="sign" />}

      {criticalCount > 0 && (
        // Escalation already happened at entry; this is the reminder that it
        // is still sitting in a queue.
        <Alert tone="danger" title={`${criticalCount} critical value${criticalCount === 1 ? '' : 's'} outstanding${unackedCount ? ` · ${unackedCount} not acknowledged` : ''}`}>
          Listed first.{unackedCount > 0 ? ' Acknowledge each on its line: who was told, and when.' : ''}
        </Alert>
      )}

      <Tabs
        tabs={[
          { key: 'ENTERED', label: 'Awaiting validation', hint: countText(c?.awaitingValidation) },
          { key: 'VALIDATED', label: 'Awaiting release', hint: countText(c?.awaitingRelease) },
          { key: 'ALL', label: 'All outstanding', hint: countText(c?.outstanding) },
        ]}
        active={stage}
        onChange={(k) => setStage(k as Stage)}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <SearchInput value={search} onValueChange={setSearch} placeholder="Subject code or test"
                     aria-label="Search by subject code or test" className="w-full sm:w-72" />
        {!pending.loading && (
          <span className="text-caption text-neutral-600" aria-live="polite">
            {loadedOf(all.length, pending.total)}
          </span>
        )}
      </div>

      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {pending.error && <ErrorState title="Could not load the queue" detail={pending.error} onRetry={pending.reload} />}
      {pending.loading && !all.length && <TextSkeleton lines={6} />}
      {!pending.loading && rows.length === 0 && !pending.hasMore && (
        <EmptyState
          title={stage === 'ENTERED' ? 'Nothing is waiting for validation' : stage === 'VALIDATED' ? 'Nothing is waiting for release' : 'Nothing is outstanding'}
          hint={term ? `Nothing matches “${term}”. Clear the search to see the whole queue.` : 'Entered results wait here to be validated and released.'}
        />
      )}

      {groups.length > 0 && (
        // A compact queue on the left, the open subject wide on the right.
        // Below lg the subject has its own view, with Back to list.
        <div className="grid items-start gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
          <nav aria-label="Subjects" className={cn('space-y-1.5', activeId && 'max-lg:hidden')}>
            <ul className="space-y-1.5">
              {groups.map(([encounterId, list]) => (
                <QueueItem key={encounterId} list={list} selected={encounterId === activeId} onOpen={() => select(encounterId)} />
              ))}
            </ul>
            <LoadMore hasMore={pending.hasMore} loading={pending.loadingMore} onLoadMore={pending.loadMore} shown={all.length} />
          </nav>
          <section className={cn('min-w-0', !activeId && 'max-lg:hidden')} aria-label="Selected subject">
            {activeId && (
              <Button variant="ghost" size="sm" className="mb-2 lg:hidden" icon={<ArrowLeft aria-hidden className="h-4 w-4" />} onClick={backToList}>
                Back to list
              </Button>
            )}
            {active && (
              <SubjectCard
                key={active[0]}
                list={active[1]}
                canValidate={canValidate}
                canRelease={canRelease}
                canAck={canAck}
                others={others}
                onAck={setAcking}
                onSendBack={setReturning}
                onError={setError}
                onChanged={reload}
              />
            )}
            {!active && selectedId && !pending.loading && (
              <EmptyState title="This subject is no longer in this list" hint="Its results may have been released, sent back, or moved to another tab." />
            )}
          </section>
        </div>
      )}

      {returning && (
        <SendBackDialog
          row={returning}
          onClose={() => setReturning(null)}
          onDone={() => { setReturning(null); reload(); }}
        />
      )}

      {acking && (
        <AcknowledgeDialog
          row={acking}
          onClose={() => setAcking(null)}
          onDone={() => { setAcking(null); reload(); }}
        />
      )}
    </div>
  );
}



/** "a second person signs", with the name when the venue has one, or the gap. */
function secondSigner(others: string[] | null): string {
  if (others === null) return 'a second person validates and releases';
  if (!others.length) return 'nobody else at this venue can validate or release — ask the venue admin to add a second Pathologist';
  return `${eitherOf(others)} validates and releases`;
}

function SubjectCard({ list, canValidate, canRelease, canAck, others, onAck, onSendBack, onError, onChanged }: {
  list: PendingResultRow[];
  canValidate: boolean;
  canRelease: boolean;
  canAck: boolean;
  /** Other signers at the venue; null when unknown. */
  others: string[] | null;
  onAck: (r: PendingResultRow) => void;
  onSendBack: (r: PendingResultRow) => void;
  onError: (e: string | null) => void;
  onChanged: () => void;
}) {
  // A subject whose rows may run onto the next page is fetched whole before
  // it is shown as one card: signing a page-partial group as if complete is
  // how a value is left out without anyone deciding to leave it out.
  // The open subject is always read whole, on its own: the queue's page may
  // hold only part of it, and a part must never be signed as the whole.
  const whole = useQuery<CursorPage<PendingResultRow>>(
    () => api.get<CursorPage<PendingResultRow>>(`/lab/results?encounterId=${list[0]!.encounterId}&stage=ALL&limit=100`),
    [list[0]!.encounterId, list.length],
  );
  const complete = !!whole.data && !whole.data.nextCursor;
  if (whole.data?.items.length) list = whole.data.items;
  const encounterId = list[0]!.encounterId;
  const { permissions } = useAuth();
  const links = useLabLinks();
  const canReports = holds(permissions, 'lab.worklist-read');
  // What a release left: the report ready to verify, or what still stands in its way.
  const [after, setAfter] = useState<{ released: number; report: LabReport | null } | null>(null);
  async function readReport(released: number) {
    try {
      const pg = await api.get<LabReportPage>(`/lab/reports?encounterId=${encounterId}&page=1&limit=1`);
      const latest = pg.items[0];
      setAfter({ released, report: latest ? await api.get<LabReport>(`/lab/reports/${latest.id}`) : null });
    } catch { setAfter({ released, report: null }); }
  }
  const subject = list[0]!.subject;
  const attention = list.filter((r) => r.flag !== 'NORMAL')
    .sort((a, b) => Number(isCritical(b)) - Number(isCritical(a)));
  const normal = list.filter((r) => r.flag === 'NORMAL');
  const [showNormal, setShowNormal] = useState(attention.length === 0);
  const [busy, setBusy] = useState<'validate' | 'release' | null>(null);
  // What the release confirmation is about to sign out: the whole card's
  // validated results, or the one row whose Release was pressed. Either way
  // the same confirmation — a single value reaching a fitness decision is no
  // less final than eight.
  const [confirming, setConfirming] = useState<PendingResultRow[] | null>(null);
  // The lines that did not go through last time, and why — retried on their own.
  const [failed, setFailed] = useState<{
    what: 'validate' | 'release'; done: number; total: number;
    rows: Array<{ r: PendingResultRow; why: string }>;
    skipped: Array<{ r: PendingResultRow; why: string }>;
  } | null>(null);

  // Nobody validates or releases their own entry (the API refuses both).
  // A critical value is never swept up in the batch. One click validating
  // twenty rows, one of them a panic value nobody looked at, is the signature
  // that means least exactly where it has to mean most; it is validated on its
  // own line, or sent back.
  const awaiting = list.filter((r) => r.stage === 'ENTERED' && !r.enteredByYou);
  const toValidate = awaiting.filter((r) => !isCritical(r));
  const criticalAwaiting = awaiting.length - toValidate.length;
  const toRelease = list.filter((r) => r.stage === 'VALIDATED' && !r.enteredByYou);
  const ownEntries = list.filter((r) => r.enteredByYou).length;
  const singleHanded = list.filter((r) => r.singleHanded).length;

  // One signature per subject rather than one per line. A CBC is eight rows;
  // the pathologist reads them together and signs them together. A refusal on
  // one line stops there, with the rest done and the count said.
  // Every line is tried; a refusal on one does not stop the rest, and the
  // outcome names what did not go through and why.
  // Batch policy — explicit per-item partial success, NOT atomic. Each line
  // is its own signature on its own value, so one refusal (a value sent back
  // a moment ago, a separation-of-duties rule) must not undo or block the
  // others. Before acting, the subject is re-read: a line that changed since
  // it was loaded (validated, released, sent back or replaced by someone
  // else) is NOT attempted. Afterwards the outcome is told in three parts —
  // done, failed, not attempted — and only the failed lines can be retried.
  // A retry cannot write twice: validating an already-validated value is a
  // no-op on the server, and a second release is refused as already released
  // (counted here as done). Never reported as "all done" unless it was.
  async function actAll(items: PendingResultRow[], what: 'validate' | 'release') {
    setBusy(what); onError(null); setFailed(null);
    const notDone: Array<{ r: PendingResultRow; why: string }> = [];
    const skipped: Array<{ r: PendingResultRow; why: string }> = [];
    let done = 0;
    let fresh: Map<string, PendingResultRow> | null = null;
    try {
      const now = await api.get<CursorPage<PendingResultRow>>(`/lab/results?encounterId=${items[0]!.encounterId}&stage=ALL&limit=100`);
      fresh = new Map(now.items.map((x) => [x.id, x]));
    } catch { fresh = null; /* could not re-read: try each, the server still refuses what it must */ }
    for (const r of items) {
      const cur = fresh?.get(r.id);
      if (fresh && !cur) { skipped.push({ r, why: 'no longer outstanding — released, sent back or replaced since you loaded it' }); continue; }
      if (cur && what === 'validate' && cur.stage !== 'ENTERED') { skipped.push({ r, why: 'already validated by someone else' }); continue; }
      if (cur && what === 'release' && cur.stage !== 'VALIDATED') { skipped.push({ r, why: 'not validated any more' }); continue; }
      try { await api.post(`/lab/results/${r.id}/${what}`, {}); done++; }
      catch (e) {
        if (what === 'release' && e instanceof ApiError && e.type === 'result-already-released') { done++; continue; }
        notDone.push({ r, why: errorMessage(e) });
      }
    }
    setBusy(null); setConfirming(null); onChanged();
    if (notDone.length || skipped.length) setFailed({ what, rows: notDone, skipped, done, total: items.length });
    if (what === 'release' && done > 0) void readReport(done);
  }

  const releasing = confirming ?? [];
  const releasingOutside = releasing.filter((r) => r.flag !== 'NORMAL').length;
  const releasingCritical = releasing.filter(isCritical).length;
  const releasingUnacked = releasing.filter((r) => r.criticalAlert && !r.criticalAlert.acknowledgedAt).length;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-neutral-200 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-mono text-body-lg font-semibold text-neutral-900">{subject.displayName}</h2>
            {attention.some(isCritical) && (
              <StatusBadge tone="danger"><Siren aria-hidden className="h-3.5 w-3.5" /> Critical</StatusBadge>
            )}
          </div>
          <p className="text-caption font-normal text-neutral-500">
            {subjectLine(subject) || 'Sex and age not recorded'} · {list.length} result{list.length === 1 ? '' : 's'}
            {' · '}{attention.length ? `${attention.length} need${attention.length === 1 ? 's' : ''} attention` : 'all within range'}
          </p>
        </div>
      </div>

      {failed && (
        <div className="px-4 pt-3">
          <Alert tone="warning" title={`${failed.done} of ${failed.total} ${failed.what}d — not all went through`}>
            {failed.rows.length > 0 && (
              <>
                <p className="mt-1 font-medium">Failed · {failed.rows.length}</p>
                <ul className="space-y-0.5">{failed.rows.map(({ r, why }) => <li key={r.id}><b>{r.testName}</b>: {why}</li>)}</ul>
              </>
            )}
            {failed.skipped.length > 0 && (
              <>
                <p className="mt-1 font-medium">Not attempted · {failed.skipped.length}</p>
                <ul className="space-y-0.5">{failed.skipped.map(({ r, why }) => <li key={r.id}><b>{r.testName}</b>: {why}</li>)}</ul>
              </>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {failed.rows.length > 0 && (
                <Button size="sm" variant="secondary" loading={busy === failed.what}
                        onClick={() => void actAll(failed.rows.map((x) => x.r), failed.what)}>
                  Retry failed ({failed.rows.length})
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setFailed(null)}>Dismiss</Button>
            </div>
          </Alert>
        </div>
      )}

      {!complete && (
        <div className="px-4 pt-3">
          {whole.error
            ? <Alert tone="warning">The whole subject could not be read, so nothing is offered in bulk. {whole.error}</Alert>
            : <TextSkeleton lines={1} />}
        </div>
      )}

      {after && <AfterRelease after={after} encounterId={encounterId} canReports={canReports}
                              reportHref={(r) => links.to('reports', { encounter: encounterId, report: r?.id, rev: r?.revision, back: true })}
                              onDismiss={() => setAfter(null)} />}

      {attention.length > 0 && (
        <section aria-label="Findings needing attention">
          <h3 className="bg-neutral-50 px-4 py-1.5 text-micro uppercase tracking-wider text-neutral-600">
            Needs attention · {attention.length}
          </h3>
          <ColumnHeads />
          <ul className="divide-y divide-neutral-200">
            {attention.map((r) => (
              <ResultLine key={r.id} r={r} emphasis canValidate={canValidate} canRelease={canRelease} others={others}
                canAck={canAck} onAck={onAck} onSendBack={onSendBack} onRelease={(x) => setConfirming([x])} onError={onError} onChanged={onChanged} />
            ))}
          </ul>
        </section>
      )}

      {normal.length > 0 && (
        <section aria-label="Normal results" className="border-t border-neutral-200">
          <button
            type="button"
            onClick={() => setShowNormal((v) => !v)}
            aria-expanded={showNormal}
            className="flex w-full items-center justify-between gap-2 px-4 py-2 text-left hover:bg-neutral-50 max-md:min-h-10"
          >
            <span className="text-micro uppercase tracking-wider text-neutral-600">Normal · {normal.length}</span>
            <span className="inline-flex items-center gap-1 text-caption text-neutral-500">
              {showNormal ? 'Hide' : 'Show'}
              <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', showNormal && 'rotate-180')} />
            </span>
          </button>
          {showNormal && (<>
            <ColumnHeads />
            <ul className="divide-y divide-neutral-200 border-t border-neutral-200">
              {normal.map((r) => (
                <ResultLine key={r.id} r={r} canValidate={canValidate} canRelease={canRelease} others={others}
                  canAck={canAck} onAck={onAck} onSendBack={onSendBack} onRelease={(x) => setConfirming([x])} onError={onError} onChanged={onChanged} />
              ))}
            </ul>
          </>)}
        </section>
      )}

      {(canValidate || canRelease) && (
        // Held at the foot of a phone's screen: "Validate all" was under
        // fifteen rows, 2,700px down.
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 bg-neutral-50 px-4 py-3 max-md:sticky max-md:bottom-0 max-md:z-10">
          <div className="min-w-0 space-y-0.5 text-caption font-normal">
            {ownEntries > 0 && (
              <p className="text-warning-600">{ownEntries} you entered — left out; {secondSigner(others)}.</p>
            )}
            {singleHanded > 0 && (
              <p className="text-neutral-600">{singleHanded} entered and validated by one person — the release is the second pair of hands.</p>
            )}
            {canValidate && criticalAwaiting > 0 && (
              <p className="text-danger-600">
                {criticalAwaiting} critical value{criticalAwaiting === 1 ? ' is' : 's are'} left out of “validate all” — validate {criticalAwaiting === 1 ? 'it' : 'each'} on its own line, or send it back.
              </p>
            )}
            {!awaiting.length && !toRelease.length && !ownEntries && (
              <p className="text-neutral-600">Nothing here for you to sign.</p>
            )}
            {!complete && (awaiting.length > 0 || toRelease.length > 0) && (
              <p className="text-neutral-600">Reading the whole subject before anything is offered together…</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canValidate && toValidate.length > 0 && (
              <Button variant="secondary" loading={busy === 'validate'} disabled={busy !== null || !complete}
                icon={<CheckCircle2 aria-hidden className="h-4 w-4" />}
                onClick={() => actAll(toValidate, 'validate')}>
                Validate {toValidate.length === 1 ? '1 result' : `all ${toValidate.length}`}
              </Button>
            )}
            {canRelease && toRelease.length > 0 && (
              <Button variant="success" disabled={busy !== null || !complete}
                icon={<Send aria-hidden className="h-4 w-4" />}
                onClick={() => setConfirming(toRelease)}>
                Release {toRelease.length === 1 ? '1 result' : `${toRelease.length} results`}…
              </Button>
            )}
          </div>
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title={releasing.length === 1 ? `Release ${releasing[0]!.testName}?` : `Release ${releasing.length} results?`}
          what="Released results become visible to the certifying physician and to the employee, and count towards the fitness decision."
          affects={
            <>
              <span className="font-mono">{subject.displayName}</span>
              {releasing.length === 1
                ? <> · {readableValue(releasing[0]!.value)}{releasing[0]!.unit ? ` ${releasing[0]!.unit}` : ''} · {FLAG_LABEL[releasing[0]!.flag]}</>
                : <>
                  {' · '}{releasing.length} validated results
                  {releasingOutside > 0 && ` · ${releasingOutside} outside range`}
                  {releasingCritical > 0 && ` · ${releasingCritical} critical`}
                </>}
            </>
          }
          irreversible={`A released value is final: it is changed only by an amendment, recorded beside the original.${releasingUnacked ? ` ${releasingUnacked} critical value${releasingUnacked === 1 ? ' is' : 's are'} not yet acknowledged.` : ''}`}
          confirmLabel={releasing.length === 1 ? 'Release result' : `Release ${releasing.length} results`}
          tone="success"
          onConfirm={() => actAll(releasing, 'release')}
          onCancel={() => setConfirming(null)}
        />
      )}
    </Card>
  );
}

function ResultLine({ r, emphasis, canValidate, canRelease, others, canAck, onAck, onSendBack, onRelease, onError, onChanged }: {
  r: PendingResultRow;
  emphasis?: boolean;
  canValidate: boolean;
  canRelease: boolean;
  others: string[] | null;
  canAck: boolean;
  onAck: (r: PendingResultRow) => void;
  onSendBack: (r: PendingResultRow) => void;
  onRelease: (r: PendingResultRow) => void;
  onError: (e: string | null) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const critical = isCritical(r);

  async function validate(comment?: string) {
    setBusy(true); onError(null);
    try { await api.post(`/lab/results/${r.id}/validate`, comment ? { comment } : {}); onChanged(); }
    catch (e) { onError(errorMessage(e)); } finally { setBusy(false); }
  }
  // Requirement 4: a verification remark composed from the lab templates.
  // The endpoint always took one (validationComment); the screen never sent it.
  const canRemark = canCompose(useAuth().permissions, ['LAB_REMARK']);
  const [composing, setComposing] = useState(false);
  const [remark, setRemark] = useState<string | null>(null);

  // The one act this row allows this person, if any. Validate is a check,
  // done in place; Release goes through the confirmation, like the card's.
  let action: ReactNode = null;
  if (r.enteredByYou && (canValidate || canRelease)) {
    action = (
      <span className="text-caption text-warning-600">
        You entered this — {others === null ? 'a second person signs' : others.length ? `${eitherOf(others)} signs` : 'nobody else here can sign it'}
      </span>
    );
  } else if (r.stage === 'ENTERED' && canValidate) {
    action = (
      <RowActions>
        <IconAction label={`Validate ${r.testName}`} icon={CheckCircle2} primary loading={busy} onClick={() => validate()} />
        {canRemark && (
          <IconAction label={`Validate ${r.testName} with a remark`} icon={MessageSquarePlus} disabled={busy} onClick={() => setComposing(true)} />
        )}
        <IconAction label={`Send ${r.testName} back to the bench`} icon={Undo2} danger disabled={busy} onClick={() => onSendBack(r)} />
      </RowActions>
    );
  } else if (r.stage === 'VALIDATED' && canRelease) {
    action = (
      <RowActions>
        <IconAction label={`Release ${r.testName}`} icon={Send} primary onClick={() => onRelease(r)} />
      </RowActions>
    );
  }

  const band = bandText(r.referenceRange);
  const state = r.stage === 'VALIDATED' ? 'Validated' : 'Entered';
  const remarkDialogs = (
    <>
      {composing && (
        <ClinicalComposer
          contexts={['LAB_REMARK']} current=""
          onClose={() => setComposing(false)}
          onAccept={(t) => { setComposing(false); setRemark(t.slice(0, 1000)); }}
        />
      )}
      {remark !== null && (
        <Dialog
          title={`Validate ${r.testName} with this remark`} onClose={() => setRemark(null)}
          footer={(
            <div className="flex w-full justify-end gap-2 max-md:flex-col-reverse max-md:items-stretch">
              <Button variant="secondary" onClick={() => setRemark(null)}>Cancel</Button>
              <Button loading={busy} onClick={async () => { await validate(remark); setRemark(null); }}>Validate with remark</Button>
            </div>
          )}
        >
          <p className="whitespace-pre-line text-small text-neutral-900">{remark}</p>
          <p className="mt-2 text-caption text-neutral-500">The remark is kept with the result and shown at release.</p>
        </Dialog>
      )}
    </>
  );

  return (
    <li className={cn('px-4 py-2.5', critical && 'bg-danger-100/50')}>
      {remarkDialogs}
      {/* Test | Result | Unit | Reference range | Flag | Review state | act —
          a table on a wide screen, stacked on a phone. */}
      <div className={cn(COLS, 'items-center gap-y-1.5')}>
        <div className="min-w-0">
          <div className={cn('text-neutral-900', emphasis ? 'text-body font-semibold' : 'text-body')}>{r.testName}</div>
          <div className="font-mono text-caption font-normal text-neutral-500">
            {r.testCode}{r.attemptNo > 1 ? ` · attempt ${r.attemptNo}` : ''}
            <span className="ml-2 font-sans md:hidden">{band ? `ref ${band}` : ''} · {state}</span>
          </div>
        </div>
        <div className={cn('tabular-nums text-neutral-900 md:text-right', emphasis ? 'text-body-lg font-semibold' : 'text-body')}>
          {readableValue(r.value)}<span className="ml-1 text-small font-normal text-neutral-500 md:hidden">{r.unit ?? ''}</span>
        </div>
        <div className="text-small text-neutral-600 max-md:hidden">{r.unit ?? '—'}</div>
        <div className="text-small text-neutral-600 max-md:hidden">{band ?? '—'}</div>
        <div>
          <StatusBadge tone={flagTone(r.flag)}>
            {critical && <Siren aria-hidden className="h-3.5 w-3.5" />}
            {flagWord(r.flag, r.valueType)}
          </StatusBadge>
        </div>
        <div className="max-md:hidden">
          <StatusBadge tone={r.stage === 'VALIDATED' ? 'info' : 'neutral'}>{state}</StatusBadge>
        </div>
        <div className="flex justify-end">{action}</div>
      </div>
      {(r.criticalAlert || r.singleHanded || r.validationComment) && (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-caption font-normal">
          {r.criticalAlert && (r.criticalAlert.acknowledgedAt
            ? <span className="text-neutral-600">Critical acknowledged {onDateTime(r.criticalAlert.acknowledgedAt)}</span>
            : canAck
              ? (
                <button
                  type="button"
                  onClick={() => onAck(r)}
                  className="inline-flex min-h-[28px] max-md:min-h-10 items-center rounded-btn border border-danger-600/40 bg-white px-2.5 font-semibold text-danger-600 hover:bg-danger-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-600"
                >
                  Acknowledge critical…
                </button>
              )
              : <span className="font-medium text-danger-600">Critical — not yet acknowledged · a pathologist or lab admin records who was told</span>)}
          {r.singleHanded && <span className="text-warning-600">One pair of hands so far</span>}
          {r.validationComment && <span className="text-neutral-600">Note: {r.validationComment}</span>}
        </div>
      )}
      {/* The trail behind the value, out of the way until asked for. */}
      <details className="group mt-1">
        <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-caption text-neutral-500 hover:text-neutral-800 max-md:min-h-10">
          <History aria-hidden className="h-3.5 w-3.5" /> History
          <ChevronRight aria-hidden className="h-3 w-3 transition-transform group-open:rotate-90" />
        </summary>
        <dl className="mt-1 grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-0.5 rounded-input bg-neutral-50 px-3 py-2 text-caption">
          <dt className="text-neutral-500">Attempt</dt><dd>{r.attemptNo}{r.attemptNo > 1 ? ' — earlier values were sent back and re-entered' : ''}</dd>
          <dt className="text-neutral-500">Entered</dt><dd>{onDateTime(r.enteredAt)}{r.enteredByYou ? ' · by you' : ''}</dd>
          <dt className="text-neutral-500">Validated</dt><dd>{r.validatedAt ? `${onDateTime(r.validatedAt)}${r.validatedByYou ? ' · by you' : ''}` : 'Not yet'}</dd>
          {r.criticalAlert && (<>
            <dt className="text-neutral-500">Critical alert</dt>
            <dd>{r.criticalAlert.acknowledgedAt ? `Acknowledged ${onDateTime(r.criticalAlert.acknowledgedAt)}` : 'Raised at entry, not yet acknowledged'}</dd>
          </>)}
        </dl>
      </details>
    </li>
  );
}

/** The result table's columns, shared by the heads and every line. */
const COLS = 'md:grid md:grid-cols-[minmax(0,1.6fr)_minmax(4.5rem,auto)_4.5rem_minmax(0,1.1fr)_8rem_6.5rem_5.5rem] md:gap-x-3 max-md:flex max-md:flex-wrap max-md:justify-between max-md:gap-x-3';

function ColumnHeads() {
  return (
    <div className={cn(COLS, 'border-b border-neutral-200 px-4 py-1.5 text-caption font-medium text-neutral-500 max-md:hidden')}>
      <span>Test</span><span className="text-right">Result</span><span>Unit</span><span>Reference range</span>
      <span>Flag</span><span>Review state</span><span className="sr-only">Action</span>
    </div>
  );
}

const onDateTime = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

/** One subject in the queue: who, how much, and what kind. */
function QueueItem({ list, selected, onOpen }: { list: PendingResultRow[]; selected: boolean; onOpen: () => void }) {
  const subject = list[0]!.subject;
  const crit = list.filter(isCritical);
  const unacked = crit.filter((r) => r.criticalAlert && !r.criticalAlert.acknowledgedAt).length;
  const outside = list.filter((r) => r.flag !== 'NORMAL').length;
  const toValidate = list.filter((r) => r.stage === 'ENTERED').length;
  const toRelease = list.filter((r) => r.stage === 'VALIDATED').length;
  return (
    <li>
      <button
        type="button" onClick={onOpen} aria-current={selected ? 'true' : undefined}
        className={cn(
          'w-full rounded-input border px-3 py-2 text-left transition-colors duration-hover',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          selected ? 'border-primary-600 bg-primary-50 ring-1 ring-primary-600'
            : crit.length ? 'border-danger-600/40 bg-white hover:bg-neutral-50' : 'border-neutral-200 bg-white hover:bg-neutral-50',
        )}
      >
        <span className="block truncate font-mono text-small font-medium text-neutral-900">{subject.displayName}</span>
        {crit.length > 0 && (
          <StatusBadge tone="danger" className="mt-1"><Siren aria-hidden className="h-3 w-3" />{unacked ? `${unacked} critical not acknowledged` : 'Critical'}</StatusBadge>
        )}
        <div className="mt-0.5 text-caption font-normal text-neutral-600">
          {[toValidate ? `${toValidate} to validate` : null, toRelease ? `${toRelease} to release` : null,
            outside ? `${outside} outside range` : 'all within range'].filter(Boolean).join(' · ')}
        </div>
      </button>
    </li>
  );
}

/**
 * What a release left behind: the report is ready to verify, or exactly what
 * still stands in its way — from the report's typed readiness, never guessed.
 */
function AfterRelease({ after, canReports, reportHref, onDismiss }: {
  after: { released: number; report: LabReport | null };
  encounterId: string;
  canReports: boolean;
  reportHref: (r: LabReport | null) => string;
  onDismiss: () => void;
}) {
  const r = after.report;
  const blocking = (r?.readiness ?? []).filter((x) => x.blocks.length);
  const ready = !!r && (r.status === 'READY_FOR_VERIFICATION' || r.status === 'VERIFIED') && !blocking.length;
  return (
    <div className="px-4 pt-3">
      <Alert tone={ready ? 'success' : 'info'} title={`Released ${after.released} result${after.released === 1 ? '' : 's'}`}>
        {!r
          ? 'No lab report is open for this subject yet — the laboratory bench opens it once results are released.'
          : ready
            ? `${r.reportNumber} is ready to ${r.status === 'VERIFIED' ? 'sign' : 'verify'}.`
            : (
              <>
                {r.reportNumber} is not ready yet:
                <ul className="mt-1 space-y-0.5">
                  {blocking.map((b) => <li key={b.code}><span className="font-medium">{b.role}:</span> {b.message}</li>)}
                  {!blocking.length && <li>Status: {r.status.replace(/_/g, ' ').toLowerCase()}.</li>}
                </ul>
              </>
            )}
        <div className="mt-2 flex flex-wrap gap-2">
          {r && canReports && (
            <Link to={reportHref(r)} className="inline-flex min-h-8 items-center gap-1 rounded-btn border border-neutral-300 bg-white px-3 text-small font-medium text-neutral-800 hover:bg-neutral-50 max-md:min-h-10">
              <FileText aria-hidden className="h-4 w-4" /> Open the report
            </Link>
          )}
          <Button size="sm" variant="ghost" onClick={onDismiss}>Dismiss</Button>
        </div>
      </Alert>
    </div>
  );
}

/**
 * A value as a pathologist reads it. A tooth chart arrives as a JSON map of
 * tooth → finding and was printed raw ({"14":"CARIOUS"}); it reads as
 * "14 Carious · 35 Carious".
 */
export function readableValue(v: unknown): string {
  if (v == null) return '—';
  const text = String(v);
  if (text.startsWith('{') && text.endsWith('}')) {
    try {
      const map = JSON.parse(text) as Record<string, unknown>;
      const parts = Object.entries(map).map(([k, x]) => {
        const word = String(x).replace(/_/g, ' ').toLowerCase();
        return `${k} ${word.charAt(0).toUpperCase()}${word.slice(1)}`;
      });
      if (parts.length) return parts.join(' · ');
    } catch { /* not JSON after all: shown as typed */ }
  }
  return text;
}

/**
 * A reference band as a reader says it: "ref 7 – 55 · critical > 200", not
 * "critical — / 200", which left the reader to work out which side the dash
 * stood for.
 */
export function bandText(r: {
  low: number | null; high: number | null; criticalLow: number | null; criticalHigh: number | null;
} | null | undefined): string | null {
  if (!r) return null;
  const base = r.low != null && r.high != null ? `${r.low} – ${r.high}`
    : r.high != null ? `< ${r.high}` : r.low != null ? `> ${r.low}` : null;
  const crit = r.criticalLow != null && r.criticalHigh != null ? `< ${r.criticalLow} or > ${r.criticalHigh}`
    : r.criticalHigh != null ? `> ${r.criticalHigh}` : r.criticalLow != null ? `< ${r.criticalLow}` : null;
  if (!base && !crit) return null;
  return [base, crit && `critical ${crit}`].filter(Boolean).join(' · ');
}

/**
 * Sending a value back to the bench. The attempt is kept with the reason on
 * it and stops standing; the test reads as not entered until the technician
 * enters it again, which arrives here as a new attempt.
 */
function SendBackDialog({ row, onClose, onDone }: {
  row: PendingResultRow; onClose: () => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    setBusy(true); setError(null);
    try {
      await api.post(`/lab/results/${row.id}/reject`, { reason: reason.trim() });
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Send ${row.testName} back to the bench?`} onClose={onClose}>
      <p className="mt-1 text-body text-neutral-700">
        {readableValue(row.value)}{row.unit ? ` ${row.unit}` : ''} · {flagWord(row.flag, row.valueType)} · <span className="font-mono">{row.subject.displayName}</span>
      </p>
      <p className="mt-2 text-small text-neutral-600">
        This attempt stops standing and leaves your queue. The technician sees your reason on the test and enters the
        value again as a new attempt. Nothing is deleted{row.criticalAlert && !row.criticalAlert.acknowledgedAt
          ? ', and the critical alert it raised is closed with your reason on it.' : '.'}
      </p>
      <div className="mt-4">
        <TextField label="Why it is being sent back" value={reason} onChange={setReason} required autoFocus
          placeholder="0 g/dL is not possible — re-read the analyser printout"
          hint="At least five characters." info="Shown to the bench and kept on the attempt." />
      </div>
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={go} loading={busy} disabled={reason.trim().length < 5}>Send back</Button>
      </div>
    </Dialog>
  );
}

/**
 * Acknowledging a critical value. The escalation went out at entry; this is
 * the record that a person read it and what they did — "phoned the physician
 * at 14:20", not a tick. Five characters minimum, and the note is kept.
 */
function AcknowledgeDialog({ row, onClose, onDone }: {
  row: PendingResultRow; onClose: () => void; onDone: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    setBusy(true); setError(null);
    try {
      await api.post(`/lab/critical/${row.criticalAlert!.id}/acknowledge`, { note: note.trim() });
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Critical: ${row.testName}`} onClose={onClose}>
      <p className="mt-1 text-body text-neutral-700">
        {readableValue(row.value)}{row.unit ? ` ${row.unit}` : ''} · {FLAG_LABEL[row.flag]} · <span className="font-mono">{row.subject.displayName}</span>
      </p>
      <div className="mt-4">
        <TextField label="What was done" value={note} onChange={setNote} required autoFocus
          placeholder="Phoned Dr. Mehta at 14:20; repeat sample requested"
          hint="Who was told, when, and what happens next. At least five characters." info="Kept on the alert." />
      </div>
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={go} loading={busy} disabled={note.trim().length < 5}>Acknowledge</Button>
      </div>
    </Dialog>
  );
}
